import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import MobileTitleSpacer from '../components/MobileTitleSpacer';
import { tvWebProjects, palmashowPlaylists as rawPalmashowPlaylists } from '../mockData';
import { useAudio } from '../context/AudioContext';
import SEO from '../components/SEO';
import { seoConfig } from '../utils/seoConfig';

/* -------- Modale vidéo / playlist -------- */
function VideoModal({
  open,
  onClose,
  playlistId,
  videoId,
  title,
  playlists = [],
  currentPlaylistIndex = -1,
  onPlaylistNavigate,
}) {
  const gestureRef = useRef(null);
  const wheelRef = useRef(0);
  const wheelTimerRef = useRef(null);
  const wheelGestureLockRef = useRef(false);

  const canBrowsePlaylists =
    Boolean(playlistId) && playlists.length > 1 && currentPlaylistIndex >= 0;

  const browse = useCallback((direction) => {
    if (!canBrowsePlaylists) return;
    onPlaylistNavigate?.(direction);
  }, [canBrowsePlaylists, onPlaylistNavigate]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (canBrowsePlaylists && event.key === 'ArrowLeft') browse(-1);
      if (canBrowsePlaylists && event.key === 'ArrowRight') browse(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, canBrowsePlaylists, browse]);

  const origin =
    typeof window !== 'undefined' ? encodeURIComponent(window.location.origin) : '';

  const src = playlistId
    ? `https://www.youtube.com/embed/videoseries?list=${playlistId}&enablejsapi=1&origin=${origin}`
    : videoId
    ? `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${origin}`
    : '';


  if (!open) return null;

  const onTouchStart = (event) => {
    if (!canBrowsePlaylists || event.touches.length !== 1) return;
    const touch = event.touches[0];
    gestureRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const onTouchEnd = (event) => {
    const start = gestureRef.current;
    gestureRef.current = null;
    if (!start || !canBrowsePlaylists || event.changedTouches.length !== 1) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < 55 || Math.abs(dx) <= Math.abs(dy) * 1.15) return;
    browse(dx < 0 ? 1 : -1);
  };


  return (
    <div
      data-page-swipe-lock="true"
      className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onClick={onClose}
    >
      <div className="relative w-full max-w-5xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="min-w-0">
            <h3 className="text-white text-lg sm:text-xl font-bold truncate">{title || 'Vidéos'}</h3>
            {canBrowsePlaylists && (
              <p className="text-cyan-300/70 text-xs sm:text-sm mt-1">
                {currentPlaylistIndex + 1} / {playlists.length}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-full w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 text-white"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        <div className="relative">
          {canBrowsePlaylists && (
            <>
              <button
                type="button"
                onClick={() => browse(-1)}
                className="absolute z-20 left-2 sm:-left-14 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-black/75 border border-cyan-400/40 text-cyan-300 text-2xl flex items-center justify-center hover:bg-cyan-400/10"
                aria-label="Catégorie Palmashow précédente"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={() => browse(1)}
                className="absolute z-20 right-2 sm:-right-14 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-black/75 border border-cyan-400/40 text-cyan-300 text-2xl flex items-center justify-center hover:bg-cyan-400/10"
                aria-label="Catégorie Palmashow suivante"
              >
                ›
              </button>
            </>
          )}

          <div className="w-full aspect-video rounded-xl overflow-hidden border border-white/15 bg-black">
            {src ? (
              <iframe
                key={src}
                title={title || 'YouTube'}
                src={src}
                width="100%"
                height="100%"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/70">
                Aucune source vidéo
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------- util : extraire l’id vidéo depuis une URL YouTube -------- */
function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return u.pathname.slice(1);
    if (u.searchParams.get('v')) return u.searchParams.get('v');
    const m = url.match(/\/embed\/([^?&]+)/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/* ----------------------------- Page ----------------------------- */
const TvWeb = () => {
  const { setActivePlayer, miniPlayerAudioRef } = useAudio();

  // ✅ Met en pause le mini player quand on ouvre une playlist/vidéo
  const pauseMini = useCallback(() => {
    try {
      if (miniPlayerAudioRef?.current && !miniPlayerAudioRef.current.paused) {
        miniPlayerAudioRef.current.pause();
      }
    } catch (err) {
      console.warn('Erreur pause mini player :', err);
    }
  }, [miniPlayerAudioRef]);

  const palmashowPlaylists = useMemo(
    () => (Array.isArray(rawPalmashowPlaylists) ? rawPalmashowPlaylists : []),
    []
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [currentPlaylistId, setCurrentPlaylistId] = useState(null);
  const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(-1);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [modalTitle, setModalTitle] = useState('Palmashow');

  const openPlaylist = useCallback(
    (pl) => {
      pauseMini();
      setActivePlayer('external');
      setModalTitle(pl.title || 'Palmashow');
      setCurrentVideoId(null);
      setCurrentPlaylistId(pl.id || null);
      setCurrentPlaylistIndex(palmashowPlaylists.findIndex((item) => item.id === pl.id));
      setModalOpen(true);
    },
    [setActivePlayer, pauseMini, palmashowPlaylists]
  );

  const openVideo = useCallback(
    (title, videoUrl) => {
      pauseMini();
      setActivePlayer('external');
      const vid = extractYouTubeId(videoUrl);
      setModalTitle(title || 'Vidéo');
      setCurrentPlaylistId(null);
      setCurrentPlaylistIndex(-1);
      setCurrentVideoId(vid);
      setModalOpen(true);
    },
    [setActivePlayer, pauseMini]
  );

  const navigatePalmashowPlaylist = useCallback((direction) => {
    if (!palmashowPlaylists.length || currentPlaylistIndex < 0) return;
    const nextIndex =
      (currentPlaylistIndex + direction + palmashowPlaylists.length) % palmashowPlaylists.length;
    const next = palmashowPlaylists[nextIndex];
    pauseMini();
    setActivePlayer('external');
    setCurrentPlaylistIndex(nextIndex);
    setCurrentPlaylistId(next.id);
    setCurrentVideoId(null);
    setModalTitle(next.title);
  }, [currentPlaylistIndex, palmashowPlaylists, pauseMini, setActivePlayer]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const featured = tvWebProjects.find((p) => p.featured);
  const otherProjects = tvWebProjects.filter((p) => !p.featured);

  return (
    <>
      <SEO 
        title={seoConfig.tvweb.title}
        description={seoConfig.tvweb.description}
        keywords={seoConfig.tvweb.keywords}
        url={seoConfig.tvweb.url}
      />
      <div className="min-h-screen bg-black pt-20 flex flex-col">
        {/* Header */}
        <div className="relative overflow-hidden text-left px-4 sm:px-6 lg:px-8">
        {/* décor */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              'radial-gradient(circle at 30% 50%, rgba(0, 255, 255, 0.3) 0%, transparent 70%)',
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto py-12 sm:py-16 md:py-20">
          {/* ⬇️ Ajuste la valeur pour remonter/descendre sur mobile */}
          <MobileTitleSpacer height="50px" />
          <h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-3 sm:mb-4 md:mb-6"
            style={{ color: '#00CCCC' }}
          >
            TV/WEB
          </h1>
          <div
            className="h-1 w-32 md:w-40 mb-4 md:mb-6"
            style={{ background: 'linear-gradient(to right, #00CCCC, transparent)' }}
          />
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl">
            Compositeur · Preneur de son · Post Production
          </p>
        </div>
      </div>

      {/* Featured Project (Palmashow) */}
      {featured && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 sm:-mt-8 md:-mt-10 lg:mt-0 mb-10 md:mb-12">
          <div
            className="relative rounded-3xl overflow-hidden p-6 sm:p-8 md:p-12 border-2 transition-transform duration-500 hover:scale-[1.01]"
            style={{
              background:
                'linear-gradient(135deg, rgba(0, 206, 209, 0.1) 0%, rgba(0, 0, 0, 0.9) 100%)',
              borderColor: 'rgba(0, 206, 209, 0.3)',
              boxShadow: '0 0 60px rgba(0, 206, 209, 0.2)',
            }}
          >
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center">
              <div className="w-full md:w-1/3">
                <img
                  src={featured.image}
                  alt={featured.title}
                  loading="lazy"
                  className="rounded-2xl w-full aspect-square object-cover shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
              </div>

              <div className="flex-1">
                <h2
                  className="text-3xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4 md:mb-6"
                  style={{ color: '#00CED1' }}
                >
                  {featured.title}
                </h2>
                <p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-6">
                  {featured.description}
                </p>

                {/* Boutons playlists Palmashow */}
                {palmashowPlaylists.length > 0 && (
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {palmashowPlaylists.map((pl) => (
                      <button
                        key={pl.id}
                        onClick={() => openPlaylist(pl)}
                        className="px-3 py-1.5 text-xs sm:text-sm rounded-full font-medium
                                   bg-black/40 border border-cyan-400/40 text-cyan-200
                                   hover:bg-cyan-400/10 hover:border-cyan-300/70
                                   transition-all duration-300"
                      >
                        {pl.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Projects */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16 md:pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {otherProjects.map((project) => (
            <div
              key={project.id}
              className="group relative overflow-hidden rounded-2xl aspect-square bg-gray-900"
            >
              {project.link ? (
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full relative z-0 outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70 rounded-2xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent z-10" />
                  <img
                    src={project.image}
                    alt={project.title}
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 z-20 p-4 sm:p-5 md:p-6 flex flex-col justify-end">
                    <h3 className="text-white text-xl sm:text-2xl font-black mb-1 sm:mb-2">
                      {project.title}
                    </h3>
                    <p className="text-gray-300 text-xs sm:text-sm">
                      {project.description}
                    </p>
                  </div>
                </a>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      {/* Modale vidéo */}
      <VideoModal
        open={modalOpen}
        onClose={closeModal}
        playlistId={currentPlaylistId}
        videoId={currentVideoId}
        title={modalTitle}
        playlists={palmashowPlaylists}
        currentPlaylistIndex={currentPlaylistIndex}
        onPlaylistNavigate={navigatePalmashowPlaylist}
      />
      </div>
    </>
  );
};

export default TvWeb;
